<template>
  <v-dialog v-model="dialog" scrollable width="650">
    <template v-slot:activator="{ on }">
      <v-btn icon small v-if="show" v-on="on">
        <v-icon small>fas fa-history</v-icon>
      </v-btn>
    </template>
    <v-card>
      <v-toolbar elevation="0">
        <v-toolbar-title
          ><h2>{{ attributeLabel }} Value History</h2></v-toolbar-title
        >
        <v-spacer></v-spacer>
        <v-btn icon @click="dialog = false">
          <v-icon small>fas fa-times</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-data-table
          :headers="headerList"
          :items="valueHistory"
          disable-pagination
          hide-default-footer
          hide-default-header
          style="height: 300px"
        >
          <template v-slot:no-data>
            This value does not have an edit history yet
          </template>

          <template v-slot:item.value="{ value }"
            ><span v-html="value"></span
          ></template>

          <template v-slot:item.auditUserId="{ value }">
            {{ formatUsername(value) }}
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" outlined @click="dialog = false"> Close </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { get, keyBy } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
import axios from "axios";
import moment from "moment";

import resolver from "../../../../../lib/resolver";

export default {
  computed: {
    attributeLabel() {
      const { attribute, attributeMapByName } = this;

      const definition = attributeMapByName[attribute];

      if (!definition) {
        return;
      }
      const { label } = definition;

      return label;
    },
    attributeList() {
      const { entityName } = this;

      return this.attributeFilter({ entityName });
    },
    attributeMapByName() {
      const { attributeList } = this;

      return keyBy(attributeList, "name");
    },
    primaryKeyName() {
      const { entityName } = this;

      const primaryKey = this.attributeList.find(
        attribute => !!attribute.primaryKey
      );

      if (!primaryKey) {
        throw new Error(`No primary key found for ${entityName}`);
      }

      return get(primaryKey, "name");
    },
    userMapById() {
      const { userList } = this;

      return keyBy(userList, "userId");
    },
    ...mapGetters({
      attributeFilter: "attribute/filter",
    }),
    ...mapState({
      userList: state => state.user.list,
    }),
  },
  async created() {
    const { attributeName, entityName, id, primaryKeyName } = this;

    await Promise.all([
      this.attributeGetList({ reload: false }),
      this.userGetList({ reload: false }),
    ]);

    const url = `/api/${entityName}AuditHistory`;

    let auditHistoryRecordList;
    try {
      ({
        data: { result: auditHistoryRecordList },
      } = await axios({
        url,
        params: {
          filter: { [primaryKeyName]: id },
        },
      }));
    } catch (e) {
      console.error(e);
      return;
    }

    if (
      !Array.isArray(auditHistoryRecordList) ||
      !auditHistoryRecordList.length
    ) {
      this.show = false;
      return;
    }

    const promiseList = auditHistoryRecordList.map(async record => {
      const { attributeMap, auditTimestamp, auditUserId } = record;

      const attribute = this.attributeMapByName[attributeName];
      const value = attributeMap[attributeName];
      const { fkName } = attribute;

      const recordFormatted = { auditUserId };

      recordFormatted.auditTimestamp = moment(auditTimestamp).local();

      if (value === undefined || value === null) {
        return recordFormatted;
      }

      if (!fkName) {
        recordFormatted.value = value;

        return recordFormatted;
      }

      // Only fks (both single and multi) should be left to handle
      let fkIdList = value;

      // Wrap value in array if it is not an array to handle badly formatted data that has been saved
      if (!Array.isArray(fkIdList)) {
        fkIdList = [fkIdList];
      }

      fkIdList = fkIdList.filter(item => item !== undefined && item !== null);

      if (!Array.isArray(fkIdList)) {
        recordFormatted.value = value;

        return recordFormatted;
      }

      const valueListFormatted = await Promise.all(
        fkIdList.map(async fkId => {
          let resolvedEntity;
          try {
            resolvedEntity = await resolver.resolveFk(fkId, attribute);
          } catch (e) {
            console.error(e);

            return fkId;
          }

          if (!resolvedEntity) {
            return fkId;
          }

          return resolvedEntity.readableText;
        })
      );

      const valueListFormattedString = valueListFormatted.join("<br>");

      recordFormatted.value = valueListFormattedString;

      return recordFormatted;
    });

    let valueHistory = await Promise.all(promiseList);

    // Format dat after sorting
    valueHistory.forEach(record => {
      record.auditTimestamp = record.auditTimestamp.format("YYYY-MM-DD HH:mm");
    });

    valueHistory = valueHistory.reduce((acc, record) => {
      if (!acc.length) {
        if (record.value !== undefined) {
          acc.push(record);
        }

        return acc;
      }

      const previousRecord = acc[acc.length - 1];

      if (previousRecord.value === record.value) {
        return acc;
      }

      acc.push(record);

      return acc;
    }, []);

    // Show oldest at the top
    valueHistory = valueHistory.reverse();

    this.valueHistory = valueHistory;
  },
  data() {
    return {
      dialog: false,
      headerList: [
        { value: "value" },
        { value: "auditTimestamp" },
        { value: "auditUserId" },
      ],
      show: true,
      valueHistory: [],
    };
  },
  methods: {
    formatUsername(userId) {
      const { userMapById } = this;

      const user = userMapById[userId];

      if (!user) {
        return "--";
      }

      const { firstName, lastName } = user;

      return `${firstName} ${lastName}`;
    },
    ...mapActions({
      attributeGetList: "attribute/list",
      userGetList: "user/list",
    }),
  },
  props: {
    attributeName: {
      required: true,
      type: String,
    },
    entityName: {
      required: true,
      type: String,
    },
    id: {
      required: true,
    },
  },
};
</script>
